// Pure helpers for merging stock items (kitchen flow, go-live).
import { getCompatibleUnits } from '@/lib/inventory-units'
import type { FlowItem } from './types'
import type { MergeBulkSummary, MergeGroupOutcome, MergeResult } from './mergeTypes'
import { MG } from './mergeStrings'

/** Items the merged one may be folded into: active, not itself, not prep, same returnable kind. */
export function mergeCandidates(all: FlowItem[], merged: { id: string; is_returnable?: boolean }, search: string, limit = 12): FlowItem[] {
  const q = search.trim().toLowerCase()
  if (!q) return []
  return all
    .filter((i) => i.id !== merged.id && !i.is_prep && Boolean(i.is_returnable) === Boolean(merged.is_returnable))
    .filter((i) => i.name.toLowerCase().includes(q) || (i.code ?? '').toLowerCase().includes(q))
    .slice(0, limit)
}

/** True when the server can convert between the units on its own (same unit or same family). */
export function unitsConvert(from: string, to: string): boolean {
  const f = from.trim().toLowerCase()
  const t = to.trim().toLowerCase()
  return f === t || getCompatibleUnits(f).includes(t)
}

/** "3 barcodes · 2 lots · 1 menu recipe line" — what else moved, in plain words. */
export function describeMoved(moved: Record<string, number>): string {
  return Object.entries(moved)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => {
      const words = MG.moved[k]
      return `${n} ${words ? words[n === 1 ? 0 : 1] : k.replace(/_/g, ' ')}`
    })
    .join(' · ')
}

/** Every item folded into the group's survivor, by name. */
export function mergedNames(o: MergeGroupOutcome): string[] {
  return o.merges.map((m: MergeResult) => m.merged_name)
}

/** Groups to list first: what will change, then refusals, then the rest. */
export function sortOutcomes(s: MergeBulkSummary): MergeGroupOutcome[] {
  const rank: Record<string, number> = { merge: 0, merged: 0, refused: 1, already_merged: 2, skipped: 3 }
  return [...s.outcomes].sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || a.group.localeCompare(b.group))
}

/** Apply is offered only for a dry run with something to merge. */
export function canApplyBulk(s: MergeBulkSummary | null): boolean {
  return Boolean(s && s.dry_run && s.groups_merged > 0)
}
